import React from 'react';
import { StarsIcon } from '@revfluence/fresh-icons/solid/esm';
import { Badge } from '@/shadcn/components/ui/badge';
import { cn } from '@/shadcn/lib/utils';

export interface CardBadgeItem {
  /** Badge text content */
  text: string;
  /** Badge icon */
  icon?: React.ReactNode;
  /** Badge variant */
  variant?: 'default' | 'secondary' | 'destructive' | 'outline';
  /** Custom badge className */
  className?: string;
  /** Unique key for the badge */
  key?: string;
}

export interface CardBadgeSectionProps {
  /** Array of badge items to display */
  badges?: CardBadgeItem[];
  /** Show boost eligible badge */
  showBoostEligible?: boolean;
  /** Whether content is boost eligible */
  isBoostEligible?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Custom children badges */
  children?: React.ReactNode;
}

/**
 * CardBadgeSection
 * ----------------
 * Renders a section of badges in a card, typically used for status indicators.
 * Supports boost eligible badge and custom badge collections.
 *
 * Features:
 * - Built-in boost eligible badge with icon
 * - Custom badge array support
 * - Flexible badge variants and styling
 * - Responsive badge layout
 * - Conditional badge rendering
 */
export const CardBadgeSection = React.forwardRef<HTMLDivElement, CardBadgeSectionProps>(
  ({
    badges = [],
    showBoostEligible = true,
    isBoostEligible = false,
    className,
    children,
    ...props
  }, ref) => {
    const hasBoostBadge = showBoostEligible && isBoostEligible;
    const hasBadges = badges.length > 0;
    const hasChildren = Boolean(children);

    // Don't render if no badges to show
    if (!hasBoostBadge && !hasBadges && !hasChildren) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={cn('flex justify-start', className)}
        {...props}
      >
        <div className="flex flex-wrap gap-2">
          {/* Boost Eligible Badge */}
          {hasBoostBadge && (
            <Badge
              variant="secondary"
              className="bg-green-4 text-white text-xs px-2 py-1 flex items-center gap-1"
            >
              <StarsIcon className="h-3 w-3" />
              Boost Eligible
            </Badge>
          )}

          {/* Custom Badges */}
          {badges.map((badge, index) => (
            <Badge
              key={badge.key || `badge-${index}`}
              variant={badge.variant || 'secondary'}
              className={cn(
                'text-xs px-2 py-1 flex items-center gap-1',
                badge.className
              )}
            >
              {badge.icon}
              {badge.text}
            </Badge>
          ))}

          {/* Custom Children */}
          {children}
        </div>
      </div>
    );
  }
);

CardBadgeSection.displayName = 'CardBadgeSection';
